import { NgTemplateOutlet, NgIf } from '@angular/common';
import { Component, Input, input } from '@angular/core';

/** Shared presentation only; modules retain their own context, commands and completion rules. */
@Component({
  selector: 'ez-workspace-frame',
  standalone: true,
  imports:[NgTemplateOutlet,NgIf],
  host: { '[class.scroll-split]': 'independentScrolling' },
  template: ` <div class="navigation"><ng-content select="[frameToolbar]" /></div>
    <div class="summary"><ng-content select="[frameSummary]" /></div>
    <div class="actions"><ng-content select="[frameActions]" /></div>
    <div class="requirements"><ng-content select="[frameRequirements]" /></div>
    <div
      class="body"
      [class.side]="hasRequirementsRail || contextPlacement === 'side'"
      [class.context-first]="contextFirstOnSmallScreens"
      [class.compact-side-rail]="compactSideRail()"
      [class.below]="contextPlacement === 'below'"
    >
      <div class="main" tabindex="0" role="region" aria-label="Main page content"><ng-content select="[frameMain]" />
        <div *ngIf="contextPlacement === 'below'" class="below-context"><ng-container [ngTemplateOutlet]="contextContent" /></div>
      </div>
      <aside
        aria-label="Workspace context" tabindex="0"
        [hidden]="contextPlacement === 'hidden' && !hasRequirementsRail"
      >
        <ng-content select="[frameRequirementsRail]" />
        <ng-container *ngIf="contextPlacement === 'side'" [ngTemplateOutlet]="contextContent" />
      </aside>
    </div>
    <ng-template #contextContent><ng-content select="[frameContext]" /></ng-template>`,
  styles: [
    `
      :host {
        display: block;
        min-width: 0;
        container-type: inline-size;
      }
      .summary,
      .actions,
      .requirements,
      .main,
      aside {
        min-width: 0;
      }
      .body {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: var(--workspace-gap, 10px);
        align-items: start;
        padding: var(--workspace-body-padding, 0);
      }
      .below-context { margin-top:16px; min-width:0; }
      aside[hidden] {
        display: none;
      }
      aside { display:flex; flex-direction:column; gap:var(--workspace-gap, 10px); }
      aside > * { flex-shrink:0; }
      .body.side.context-first aside { grid-row:1; }
      .body.side.context-first .main { grid-row:2; }
      :host(.scroll-split) { display:flex; flex-direction:column; height:100%; min-height:0; }
      :host(.scroll-split) .body { flex:1 1 0; min-height:0; overflow:auto; }
      .main:focus-visible,
      aside:focus-visible {
        outline: 2px solid var(--ui-focus-ring-color, Highlight);
        outline-offset: -2px;
      }
      @container (min-width:700px) {
        .body.side.compact-side-rail {
          grid-template-columns: minmax(280px, 1fr) minmax(280px, 320px);
        }
        .body.side.compact-side-rail > .main { grid-row:1; grid-column:1; }
        .body.side.compact-side-rail > aside { grid-row:1; grid-column:2; }
      }
      @container (min-width:1000px) {
        .body.side.context-first aside { grid-row:1; grid-column:2; }
        .body.side.context-first .main { grid-row:1; grid-column:1; }
        .body.side {
          grid-template-columns: minmax(0, 1fr) minmax(260px, 320px);
        }
        :host(.scroll-split) .body.side { overflow:hidden; align-items:stretch; }
        :host(.scroll-split) .body.side > .main,
        :host(.scroll-split) .body.side > aside { min-height:0; overflow:auto; overscroll-behavior:contain; scrollbar-gutter:stable; }
        :host(.scroll-split) .body.side > aside { align-self:start; max-height:100%; }
      }
    `,
  ],
})
export class WorkspaceFrameComponent {
  /** Keeps a compact side rail beside the main region at container widths of 700px or more. */
  readonly compactSideRail = input(false);
  @Input() independentScrolling = false;
  @Input() hasRequirementsRail = false;
  @Input() contextFirstOnSmallScreens = false;
  @Input() contextPlacement: 'side' | 'below' | 'hidden' = 'side';
}
